import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-[#1B4D3E]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Contact Us</h1>
            <p className="text-[#D4AF37] text-lg">We'd love to hear from you</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-3xl text-[#1C1C1C] mb-6">Get In Touch</h2>
                <p className="text-[#66605B] leading-relaxed">
                  Have questions about Big Bear Estate or want to inquire about availability? 
                  We're here to help make your Kawartha getaway unforgettable.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1B4D3E]/10 rounded-lg">
                    <Phone className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1C1C] mb-1">Phone</h3>
                    <a href="tel:416-543-4749" className="text-[#1B4D3E] hover:underline text-lg">
                      416-543-4749
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1B4D3E]/10 rounded-lg">
                    <Mail className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1C1C] mb-1">Email</h3>
                    <a href="mailto:info@kawartha-luxury-properties.com" className="text-[#1B4D3E] hover:underline">
                      info@kawartha-luxury-properties.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1B4D3E]/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1C1C] mb-1">Property Location</h3>
                    <p className="text-[#66605B]">
                      Jack Lake, Apsley<br />
                      Ontario, Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1B4D3E]/10 rounded-lg">
                    <Clock className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1C1C1C] mb-1">Check-in / Check-out</h3>
                    <p className="text-[#66605B]">
                      Check-in: Friday 4:30 PM<br />
                      Check-out: Friday 11:00 AM
                    </p>
                  </div>
                </div>
              </div>

              {/* Property Manager Card */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-[#E5E0DB]">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">Property Manager</h3>
                <div className="space-y-2">
                  <p className="font-semibold text-[#1B4D3E]">Neill Dixon</p>
                  <p className="text-[#66605B]">Kawartha Luxury Properties</p>
                  <a href="mailto:neill@neilldixon.com" className="text-[#1B4D3E] hover:underline block">
                    neill@neilldixon.com
                  </a>
                  <a href="tel:416-543-4749" className="text-[#1B4D3E] hover:underline block">
                    416-543-4749
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Map Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-serif text-xl text-[#1C1C1C] mb-4">Location</h3>
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22800.42!2d-78.0833!3d44.7500!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cd3a5e0f8a1c8ed%3A0x1234567890abcdef!2sJack%20Lake%2C%20Apsley%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Big Bear Estate Location"
                  ></iframe>
                </div>
                <p className="text-sm text-[#66605B] mt-4">
                  Big Bear Estate is located on the west shore of Jack Lake in the beautiful Kawartha region, 
                  approximately 2 hours from downtown Toronto.
                </p>
              </div>

              <div className="bg-[#1B4D3E] p-6 rounded-lg text-white">
                <h3 className="font-serif text-xl mb-4">Ready to Book?</h3>
                <p className="text-white/80 mb-4">
                  Check our availability calendar and submit an inquiry for your preferred dates.
                </p>
                <a
                  href="/booking"
                  className="inline-block bg-white text-[#1B4D3E] px-6 py-3 rounded font-semibold hover:bg-[#D4AF37] hover:text-white transition-colors"
                >
                  View Availability
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
